import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, input, output, Signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { AdminPanel } from '../../../core/interfaces';
import { SwitchEnumPipe } from '../../pipes/switch-enum/switch-enum.pipe';

@Component({
  selector: 'app-entity-list',
  imports: [
    CommonModule,
    MatButton,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFormField,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIconButton,
    MatIcon,
    MatInput,
    MatLabel,
    MatNoDataRow,
    MatPaginator,
    MatProgressSpinner,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    MatTooltip,
    DatePipe,
    SwitchEnumPipe,
    ReactiveFormsModule,
  ],
  templateUrl: './entity-list.component.html',
  styleUrls: ['./entity-list.component.scss'],
})
export class EntityListComponent {
  public title = input.required<string>();
  public columns = input.required<AdminPanel.Entity.EntityColumn[]>();
  public data = input.required<AdminPanel.Entity.EntityType[]>();
  public totalItems = input.required<number>();
  public pageSize = input.required<number>();
  public pageIndex = input.required<number>();
  public loading = input.required<boolean>();
  /** Optional function returning extra action buttons per row */
  public customRowActions = input<((item: AdminPanel.Entity.EntityType) => AdminPanel.Entity.EntityRowAction[]) | null>(
    null,
  );
  /** Optional function to control delete button visibility. Defaults to always visible */
  public showDelete = input<((item: AdminPanel.Entity.EntityType) => boolean) | null>(null);
  /** Optional function returning extra action buttons rendered after edit (at delete slot, with separator) */
  public endRowActions = input<((item: AdminPanel.Entity.EntityType) => AdminPanel.Entity.EntityRowAction[]) | null>(
    null,
  );
  /** Optional function returning a CSS class for the row */
  public rowClass = input<((item: AdminPanel.Entity.EntityType) => string) | null>(null);

  public readonly pageChange = output<PageEvent>();
  public readonly sortChange = output<Sort>();
  public readonly searchChange = output<string>();
  public readonly action = output<AdminPanel.Entity.EntityAction>();

  public searchControl = new FormControl<string>('');

  public displayedColumns: Signal<string[]> = computed(() => [...this.columns().map((col) => col.key), 'actions']);

  public getColumnValue(element: AdminPanel.Entity.EntityType, key: string): string | unknown {
    const keyPath = key.split('.');
    let value: unknown = element;

    for (const pathKey of keyPath) {
      if (value === null || value === undefined || (typeof value !== 'object' && typeof value !== 'function')) {
        return '';
      }
      value = (value as Record<string, unknown>)[pathKey];
    }
    return value === null || value === undefined ? '' : value;
  }

  public getColumnStringValue(element: AdminPanel.Entity.EntityType, key: string): string {
    const value = this.getColumnValue(element, key);
    return Array.isArray(value) ? (value as unknown[]).join(', ') : String(value);
  }

  public getCountValue(element: AdminPanel.Entity.EntityType, key: string): number {
    const value = this.getColumnValue(element, key);
    return typeof value === 'number' ? value : Number(value) || 0;
  }

  public getCustomRowActions(item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] {
    return this.customRowActions()?.(item) ?? [];
  }

  public shouldShowDelete(item: AdminPanel.Entity.EntityType): boolean {
    return this.showDelete()?.(item) ?? true;
  }

  public getEndRowActions(item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] {
    return this.endRowActions()?.(item) ?? [];
  }

  public getRowClass(item: AdminPanel.Entity.EntityType): string {
    return this.rowClass()?.(item) ?? '';
  }

  public onPageChange(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  public onSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  public onSearch(): void {
    this.searchChange.emit(this.searchControl.value);
  }

  public onCreate(): void {
    this.action.emit({ type: 'create' });
  }

  public onEdit(item: AdminPanel.Entity.EntityType): void {
    this.action.emit({ type: 'edit', item });
  }

  public onDelete(item: AdminPanel.Entity.EntityType): void {
    this.action.emit({ type: 'delete', item });
  }

  public onCustomAction(type: AdminPanel.Entity.EntityAction['type'], item: AdminPanel.Entity.EntityType): void {
    this.action.emit({ type, item });
  }
}
